import { IconInfo, IconTrash } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { OrgSsoForm } from '../components/OrgSsoForm';
import { OrgSsoStatus } from '../components/OrgSsoStatus';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Toast } from '../components/Toast';
import { t } from '../i18n';
import { useOrgSsoAdmin } from '../lib/orgSsoAdmin';
import { canOwnerWrite, roleSettled } from '../lib/roles';
import { SESSION_TOKEN, useMe } from '../lib/session';

// Owner-only custom-SSO settings (#468). Reads `GET /v1/org-sso` and drives
// set/remove against the masked shape — sealed secrets never reach this view.
// `token` is injectable for tests (mirrors `AccountKeys`); the router mounts
// this with no props → dev session. `useOrgSsoAdmin` owns the interaction
// state/handlers so this stays a thin presenter.
//
// OWNER-ONLY BY ROLE: every `/v1/org-sso` route runs `requireOwner`, a role
// check no scope expresses, so this reads `role` (`canOwnerWrite`, unknown role
// fails open), not `lib/scope-gate.ts`. A member signed in through the org's
// SSO opened this page and collected a 403 (#7426): below owner, the read never
// fires and the page says whose it is. Unsettled shows neither.

export function AccountSso(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  // No token = prod pre-SSO: gate the whole route so save/remove controls
  // (which could only fire unauthenticated) never render. Mirrors AccountKeys.
  const hasSession = token !== null;
  const me = useMe(token);
  const owner = (): boolean => roleSettled(me) && canOwnerWrite(me.data?.role);
  const notOwner = (): boolean => roleSettled(me) && !canOwnerWrite(me.data?.role);
  const {
    query,
    busy,
    saveError,
    notice,
    confirmOpen,
    setConfirmOpen,
    removeError,
    save,
    askRemove,
    confirmRemove,
    dismissNotice,
  } = useOrgSsoAdmin(token, owner);

  // One toast surface: the save-success flash, or a failed remove (the confirm
  // stays open — a destructive action must never silently no-op).
  const toast = (): { message: string; tone: 'success' | 'error' } | null => {
    const err = removeError();
    if (err) return { message: err, tone: 'error' };
    const msg = notice();
    return msg ? { message: msg, tone: 'success' } : null;
  };

  return (
    <>
      <PageHeader title={t('account_sso.title')} desc={t('account_sso.subtitle')} />

      <Show when={hasSession} fallback={<EmptyState message={t('account_sso.no_session')} />}>
        <Show when={notOwner()}>
          <Panel title={t('account_sso.overview')} class="account-sso__overview">
            <p class="list-empty" role="status">
              {t('account_sso.owner_only')}
            </p>
          </Panel>
        </Show>
        <Show when={owner()}>
          <QueryBoundary
            query={query}
            loading={<CardGridSkeleton listClass="account-sso__loading" count={2} />}
          >
            {/* Single root, not a fragment — swapping a multi-root children array
              on refetch trips happy-dom's removeChild in tests. */}
            {(data) => (
              <div class="account-sso__body">
                <Show when={!data.custom_sso_enabled}>
                  <div class="alert alert--icon" role="alert">
                    <IconInfo />
                    <p class="alert__title">{t('account_sso.not_entitled')}</p>
                  </div>
                </Show>
                <Show when={data.protocol !== null}>
                  <OrgSsoStatus data={data} busy={busy()} onRemove={askRemove} />
                </Show>
                <Show when={data.custom_sso_enabled && data.protocol === null}>
                  <Panel title={t('account_sso.overview')} class="account-sso__overview">
                    <div class="list-empty">
                      <p class="account-sso__empty-title">{t('account_sso.empty_title')}</p>
                      <p>{t('account_sso.empty_body')}</p>
                    </div>
                  </Panel>
                </Show>
                {/* Create/edit form — entitled accounts only; SAML rows are
                  seed-provisioned and stay read-only (note in the summary). */}
                <Show when={data.custom_sso_enabled && data.protocol !== 'saml'}>
                  <OrgSsoForm
                    config={data.protocol === 'oidc' ? data.config : null}
                    busy={busy()}
                    error={saveError()}
                    onSave={(body) => void save(body)}
                  />
                </Show>
              </div>
            )}
          </QueryBoundary>
        </Show>

        <ConfirmDialog
          open={confirmOpen()}
          title={t('account_sso.confirm_remove_title')}
          desc={t('account_sso.confirm_remove')}
          media={<IconTrash />}
          cancelLabel={t('account_sso.cancel')}
          actionLabel={busy() ? t('account_sso.removing') : t('account_sso.remove')}
          destructive
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            // ConfirmDialog has no disabled state — gate double-fires here.
            if (!busy()) void confirmRemove();
          }}
        />
      </Show>

      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismissNotice}
      />
    </>
  );
}
